import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ScanResult, type WatchData } from "./api";
import { LiveFeed } from "./LiveFeed";

export function Sidebar() {
    return (
        <div className="sidebar">
            <LiveFeed />
            <WatchPanel />
            <SettingsPanel />
            <ScanPanel />
            <AddDownloadPanel />
            <CredentialsPanel />
        </div>
    );
}

function WatchPanel() {
    const qc = useQueryClient();
    const { data: watches = [] } = useQuery({ queryKey: ["watches"], queryFn: api.watches, refetchInterval: 30000 });
    const [url, setUrl] = useState("");
    const [label, setLabel] = useState("");

    const add = async () => {
        if (!url) return;
        await api.addWatch(url, label || undefined);
        setUrl(""); setLabel("");
        qc.invalidateQueries({ queryKey: ["watches"] });
    };

    return (
        <div className="panel">
            <h3>watch packages</h3>
            <div style={{ marginBottom: 8 }}>
                {watches.length === 0 && <div style={{ color: "#555", fontSize: "0.85em" }}>no packages watched</div>}
                {watches.map(w => (
                    <WatchRow key={w.id} watch={w} onUpdate={() => qc.invalidateQueries({ queryKey: ["watches"] })} />
                ))}
            </div>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.feistv.com/products/..." />
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="label (e.g. CLRG Worlds 2026)" />
            <button onClick={add}>add watch</button>
            <div style={{ fontSize: "0.7em", color: "#555", marginTop: 4 }}>auto-scans every 60s, starts downloads for new live events</div>
        </div>
    );
}

function watchLabel(w: WatchData): string {
    if (w.label) return w.label;
    // new URL() throws on malformed URLs — never let that take down the render
    try { return new URL(w.package_url).pathname.split("/").pop() || w.package_url; }
    catch { return w.package_url || "(invalid url)"; }
}

function WatchRow({ watch: w, onUpdate }: { watch: WatchData; onUpdate: () => void }) {
    const label = watchLabel(w);
    // sqlite datetime('now') strings are UTC without a zone marker; the bare
    // "YYYY-MM-DD HH:MM:SS" form is non-ISO and Safari rejects it — insert the "T"
    const lastScan = w.last_scan ? new Date(w.last_scan.replace(" ", "T") + "Z").toLocaleTimeString() : "never";
    const enabledColor = w.enabled ? "#33aa55" : "#555";

    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid #222", fontSize: "0.85em" }}>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={w.package_url}>
                <span style={{ color: enabledColor, cursor: "pointer" }} onClick={async () => { await api.toggleWatch(w.id, !w.enabled); onUpdate(); }}>
                    {w.enabled ? "●" : "○"}
                </span>{" "}
                {label}
            </span>
            <span
                style={{ color: w.dvr_backload ? "#3366aa" : "#555", fontSize: "0.8em", margin: "0 6px", cursor: "pointer", userSelect: "none", flexShrink: 0 }}
                title={w.dvr_backload ? "DVR backfill on — click to disable" : "DVR backfill off — click to backfill all archived videos"}
                onClick={async () => { await api.toggleDvr(w.id, !w.dvr_backload); onUpdate(); }}
            >DVR</span>
            <span style={{ color: "#555", fontSize: "0.8em", margin: "0 8px", flexShrink: 0 }}>{lastScan}</span>
            <span style={{ color: "#aa3333", cursor: "pointer", fontSize: "0.8em" }} onClick={async () => { if (confirm("Remove?")) { await api.removeWatch(w.id); onUpdate(); } }}>x</span>
        </div>
    );
}

function ScanPanel() {
    const qc = useQueryClient();
    const [url, setUrl] = useState("");
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState<ScanResult | null>(null);
    const [msg, setMsg] = useState("");

    const scan = async () => {
        if (!url) return;
        setScanning(true); setMsg("");
        try {
            const r = await api.scan(url);
            setResult(r);
            setMsg(`${r.live.length} live, ${r.tbd.length} upcoming, ${r.dvr.length} archived`);
        } catch (e: any) { setMsg(`Error: ${e.message}`); }
        finally { setScanning(false); }
    };

    const dlLive = async () => {
        const r = await api.downloadLive(url);
        setMsg(`Started ${r.started?.length || 0} live downloads`);
        setTimeout(() => qc.invalidateQueries({ queryKey: ["streams"] }), 2000);
    };

    const dlDvr = async () => {
        const r = await api.downloadDvr(url);
        setMsg(`Queued ${r.queued || 0} DVR downloads (max ${r.maxConcurrent})`);
        setTimeout(() => qc.invalidateQueries({ queryKey: ["streams"] }), 3000);
    };

    return (
        <div className="panel">
            <h3>manual scan</h3>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.feistv.com/products/..." />
            <button onClick={scan} disabled={scanning}>{scanning ? "scanning..." : "scan now"}</button>
            {msg && <div style={{ marginTop: 8, fontSize: "0.85em", color: "#33aa55" }}>{msg}</div>}
            {result && result.live.length > 0 && (
                <button className="btn-green" onClick={dlLive} style={{ marginTop: 8 }}>download all live</button>
            )}
            {result && result.dvr.length > 0 && (
                <button className="btn-blue" onClick={dlDvr} style={{ marginTop: 4 }}>backload all DVR ({result.dvr.length})</button>
            )}
            {result && result.events.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <ul className="product-list">
                        {result.events.map(e => (
                            <li key={e.id}>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.title}>{e.title}</span>
                                <span style={{ color: e.state === "live" ? "#da4165" : e.state === "tbd" ? "#aa8833" : "#555", fontSize: "0.8em" }}>{e.state}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function AddDownloadPanel() {
    const qc = useQueryClient();
    const [key, setKey] = useState("");
    const [pageUrl, setPageUrl] = useState("");
    const [hlsUrl, setHlsUrl] = useState("");

    const start = async () => {
        if (!key) return alert("stream key required");
        if (!pageUrl && !hlsUrl) return alert("page URL or HLS URL required");
        const r = await api.download(key, pageUrl, hlsUrl);
        if (r.error) alert(r.error);
        else { setKey(""); setPageUrl(""); setHlsUrl(""); qc.invalidateQueries({ queryKey: ["streams"] }); }
    };

    return (
        <div className="panel">
            <h3>add single download</h3>
            <input value={key} onChange={e => setKey(e.target.value)} placeholder="stream key" />
            <input value={pageUrl} onChange={e => setPageUrl(e.target.value)} placeholder="feistv.com video page URL" />
            <input value={hlsUrl} onChange={e => setHlsUrl(e.target.value)} placeholder="or direct HLS URL" />
            <button onClick={start}>start download</button>
        </div>
    );
}

function SettingsPanel() {
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
    const qc = useQueryClient();
    const [dvr, setDvr] = useState<string>("");
    const [chunks, setChunks] = useState<string>("");
    const [livePriority, setLivePriority] = useState<string>("");

    // Sync state from server on load
    if (settings && !dvr && !chunks) {
        setTimeout(() => {
            setDvr(settings.concurrentDvrDownloads || "6");
            setChunks(settings.concurrentChunksPerStream || "3");
            setLivePriority(settings.livePriority || "off");
        }, 0);
    }

    const save = async () => {
        await api.saveSettings({
            concurrentDvrDownloads: dvr,
            concurrentChunksPerStream: chunks,
            livePriority,
        });
        qc.invalidateQueries({ queryKey: ["settings"] });
    };

    const lpOptions = [
        { value: "liveOnly", label: "Live Only", desc: "pause all DVR while live streams are active" },
        { value: "livePriority", label: "Live Priority", desc: "halve DVR slots while live streams are active" },
        { value: "off", label: "Off", desc: "DVR runs at full concurrency regardless" },
    ];
    const currentLp = lpOptions.find(o => o.value === (livePriority || "off"))!;

    return (
        <div className="panel">
            <h3>concurrency</h3>

            <label style={{ fontSize: "0.8em", color: "#888" }}>Live priority</label>
            <div style={{ display: "flex", gap: 2, marginBottom: 4 }}>
                {lpOptions.map(o => (
                    <button key={o.value} className="btn btn-sm"
                        style={{ flex: 1, background: livePriority === o.value ? "#da4165" : "#333", fontSize: "0.75em" }}
                        onClick={() => setLivePriority(o.value)}>
                        {o.label}
                    </button>
                ))}
            </div>
            <div style={{ fontSize: "0.7em", color: "#555", marginBottom: 8 }}>{currentLp.desc}</div>

            <label style={{ fontSize: "0.8em", color: "#888" }}>DVR streams (1-10)</label>
            <input type="range" min="1" max="10" value={dvr || "6"} onChange={e => setDvr(e.target.value)}
                style={{ width: "100%", marginBottom: 4 }} />
            <div style={{ fontSize: "0.85em", marginBottom: 8 }}>{dvr || "6"} concurrent DVR downloads</div>

            <label style={{ fontSize: "0.8em", color: "#888" }}>Chunks per stream (1-20)</label>
            <input type="range" min="1" max="20" value={chunks || "3"} onChange={e => setChunks(e.target.value)}
                style={{ width: "100%", marginBottom: 4 }} />
            <div style={{ fontSize: "0.85em", marginBottom: 8 }}>{chunks || "3"} concurrent chunks/stream</div>

            <button onClick={save}>apply</button>
            <div style={{ fontSize: "0.7em", color: "#555", marginTop: 4 }}>applies to new downloads. restart running ones to take effect.</div>
        </div>
    );
}

function CredentialsPanel() {
    const [user, setUser] = useState("");
    const [pass, setPass] = useState("");

    const save = async () => {
        if (!user || !pass) return alert("both fields required");
        const r = await api.saveCreds(user, pass);
        if (r.ok) { alert("credentials updated"); setUser(""); setPass(""); }
    };

    return (
        <div className="panel">
            <h3>credentials</h3>
            <input type="email" value={user} onChange={e => setUser(e.target.value)} placeholder="VHX email" />
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="VHX password" />
            <button onClick={save}>update login</button>
            <div style={{ fontSize: "0.75em", color: "#555", marginTop: 6 }}>stored encrypted. never displayed.</div>
        </div>
    );
}
